import * as Sentry from '@sentry/vue';

export const BETTERSTACK_LOGTAIL_HOST = import.meta.env.VITE_BETTERSTACK_LOGTAIL_HOST;
export const BETTERSTACK_SECURE_TOKEN = import.meta.env.VITE_BETTERSTACK_SECURE_TOKEN;
export const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
export const DEBUG = import.meta.env.MODE !== 'production';
export const INTERCOM_APP_ID = import.meta.env.VITE_INTERCOM_APP_ID;
export const IPINFO_IO_TOKEN = import.meta.env.VITE_IPINFO_IO_TOKEN;
export const MAILOSAUR_DOMAIN = import.meta.env.VITE_MAILOSAUR_DOMAIN;
export const POSTHOG_APP_ID = import.meta.env.VITE_POSTHOG_APP_ID;
// Overridable so PostHog can be served through a first-party proxy without a code change.
export const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com';
export const PRODUCTION = import.meta.env.VITE_ENVIRONMENT === 'production';
export const STRIPE_SOLAR_ACTIVITIES_LOG_ACCESS_PLAN_ID = import.meta.env
    .VITE_STRIPE_SOLAR_ACTIVITIES_LOG_ACCESS_PLAN_ID;
export const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
export const VUEX_KEY = import.meta.env.VITE_VUEX_KEY;
export const YOUTUBE_API_KEY = import.meta.env.VITE_YOUTUBE_API_KEY;

export const isChunkLoadError = err => {
    return (
        err?.name === 'ChunkLoadError' ||
        err?.message?.includes('Loading chunk') ||
        err?.message?.includes('Loading CSS chunk') ||
        err?.message?.includes('Failed to fetch dynamically imported module') ||
        err?.message?.includes('error loading dynamically imported module') ||
        err?.message?.includes('Importing a module script failed') ||
        err?.message?.includes('Unable to preload CSS')
    );
};

const RELOAD_KEY = 'chunk_error_reload';
const RELOAD_COOLDOWN_MS = 10000;
let lastReloadAt = 0;

const reloadOnChunkError = () => {
    try {
        const last = Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
        if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    } catch {
        // sessionStorage blocked (Safari private mode, some WKWebViews) — fall back to in-memory
        if (Date.now() - lastReloadAt < RELOAD_COOLDOWN_MS) return false;
        lastReloadAt = Date.now();
    }

    window.location.reload();
    return true;
};

export const handleGlobalError = (err, vm, info) => {
    const isChunkErr = isChunkLoadError(err);

    // Chunk load errors come from stale deployments. If a reload fires, suspend the
    // caller so Vue Router's dynamic import .catch() doesn't resolve to undefined
    // before the page refreshes. If the cooldown blocks the reload, fall through and
    // let the error surface so the user isn't frozen on a blank route.
    if (isChunkErr && reloadOnChunkError()) {
        return new Promise(() => {});
    }

    const message = err?.message || 'Unknown error';
    const stack = err?.stack || '';

    const details = {
        type: 'Global error',
        message,
        stack,
        info,
        url: window?.location?.href,
        time: new Date().toISOString(),
    };

    if (vm?.$store) {
        vm.$store.commit('error/updateMessage', message);
        vm.$store.commit('error/updateStack', stack);
    }

    if (!DEBUG) {
        Sentry.captureException(err, {
            extra: details,
            tags: {
                errorType: 'GlobalError',
            },
        });
    } else {
        console.error('⚠️ Error:', err, vm, info);
    }

    // Re-throw so Vue Router's dynamic import .catch() doesn't resolve to undefined.
    throw err;
};
